import { useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { useDI } from "@infra/di/useDI";
import { LEGAL_DOCUMENTS } from "@/domain/model/LegalDocuments";
import { LegalDocumentLink } from "../components/LegalDocumentLink";
import {
  AuthActions,
  AuthBrand,
  AuthCard,
  AuthContent,
  AuthHeader,
  AuthPage,
  layout,
} from "../layout";
import {
  createTrialAccountSchema,
  type CreateTrialAccountForm,
} from "./CreateTrialAccountSchema";
import { useCreateTrialAccountViewModel } from "./CreateTrialAccountViewModel";

export default function CreateTrialAccountPage() {
  const navigate = useNavigate();
  const { createTrialAccount } = useDI();
  const { state, intents } = useCreateTrialAccountViewModel(createTrialAccount);
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<CreateTrialAccountForm>({
    resolver: zodResolver(createTrialAccountSchema),
    mode: "onBlur",
    defaultValues: {
      accountName: "",
      ownerName: "",
      email: "",
      password: "",
      seats: 1,
      acceptLegal: false,
    },
  });
  const acceptLegal = useWatch({ control, name: "acceptLegal" });
  const busy =
    isSubmitting || state.status === "loading" || state.status === "success";

  const onSubmit = async (values: CreateTrialAccountForm) => {
    const result = await intents.submit(values);
    if (result.type === "SUCCESS") navigate("/dashboard", { replace: true });
  };

  return (
    <AuthPage placement="top" sx={{ backgroundImage: "none" }}>
      <AuthCard>
        <AuthContent>
          <AuthBrand />
          <AuthHeader
            title="Kostenlos testen"
            subtitle="Erstellen Sie Ihr Logami-Testkonto für Ihren Betrieb."
          />
          {state.status === "error" && (
            <Alert severity="error">{state.message}</Alert>
          )}
          <Stack
            component="form"
            spacing={layout.form.fieldGap}
            onSubmit={handleSubmit(onSubmit)}
            noValidate
            aria-busy={busy}
          >
            <TextField
              label="Firmenname / Kontoname"
              autoComplete="organization"
              fullWidth
              required
              disabled={busy}
              error={!!errors.accountName}
              helperText={errors.accountName?.message}
              {...register("accountName")}
            />
            <TextField
              label="Ihr vollständiger Name"
              autoComplete="name"
              fullWidth
              required
              disabled={busy}
              error={!!errors.ownerName}
              helperText={errors.ownerName?.message}
              {...register("ownerName")}
            />
            <TextField
              label="E-Mail-Adresse"
              type="email"
              autoComplete="email"
              fullWidth
              required
              disabled={busy}
              error={!!errors.email}
              helperText={errors.email?.message}
              {...register("email")}
            />
            <TextField
              label="Passwort"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              fullWidth
              required
              disabled={busy}
              error={!!errors.password}
              helperText={errors.password?.message ?? "Mindestens 10 Zeichen"}
              {...register("password")}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        type="button"
                        disabled={busy}
                        aria-label={
                          showPassword
                            ? "Passwort ausblenden"
                            : "Passwort anzeigen"
                        }
                        aria-pressed={showPassword}
                        onClick={() => setShowPassword((value) => !value)}
                        onMouseDown={(event) => event.preventDefault()}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <TextField
              label="Anzahl der Zugänge"
              type="number"
              fullWidth
              required
              disabled={busy}
              error={!!errors.seats}
              helperText={
                errors.seats?.message ??
                "Alle Personen mit eigenem Zugang, einschließlich Ihnen."
              }
              slotProps={{ htmlInput: { min: 1, max: 500, step: 1 } }}
              {...register("seats", { valueAsNumber: true })}
            />
            <Controller
              name="acceptLegal"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl error={!!fieldState.error}>
                  <FormControlLabel
                    sx={{ alignItems: "flex-start", m: 0 }}
                    control={
                      <Checkbox
                        name={field.name}
                        checked={field.value}
                        onChange={(_, checked) => field.onChange(checked)}
                        onBlur={field.onBlur}
                        disabled={busy}
                        slotProps={{
                          input: {
                            ref: field.ref,
                            "aria-describedby": fieldState.error
                              ? "trial-legal-error"
                              : undefined,
                            "aria-required": true,
                          },
                        }}
                      />
                    }
                    label={
                      <Typography
                        variant="body2"
                        sx={{ pt: layout.form.legalLabelPaddingTop }}
                      >
                        Ich akzeptiere die{" "}
                        <LegalDocumentLink document={LEGAL_DOCUMENTS.terms} />{" "}
                        einschließlich des{" "}
                        <LegalDocumentLink document={LEGAL_DOCUMENTS.avv} /> und
                        der{" "}
                        <LegalDocumentLink
                          document={LEGAL_DOCUMENTS.userTerms}
                        />{" "}
                        und habe die{" "}
                        <LegalDocumentLink document={LEGAL_DOCUMENTS.privacy} />{" "}
                        zur Kenntnis genommen.
                      </Typography>
                    }
                  />
                  {fieldState.error && (
                    <FormHelperText id="trial-legal-error">
                      {fieldState.error.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
            <AuthActions align="between">
              <Button component={RouterLink} to="/login">
                Anmelden
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={!acceptLegal || busy}
              >
                {busy ? "Testkonto wird erstellt..." : "Kostenlos testen"}
              </Button>
            </AuthActions>
          </Stack>
        </AuthContent>
      </AuthCard>
    </AuthPage>
  );
}
